import React, { useState, useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { Box, Paper, Tab, Tabs, Button, Stack } from "@mui/material";
import {
  Medication,
  Science,
  Description,
  InfoOutlined,
  Save,
  CheckCircle,
  Settings,
  Assignment,
} from "@mui/icons-material";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import { TreatmentsSection } from "./TreatmentsSection";
import { ExamsProceduresSection } from "./ExamsProceduresSection";
import { ReportsSection } from "./ReportsSection";
import { GuidelinesSection } from "./GuidelinesSection";
import VetProtocolQuestionnairesPicker from "./VetProtocolQuestionnairesPicker";
import VetPetQuestionnairesPanel from "./VetPetQuestionnairesPanel";
import { TemplateSaver } from "./TemplateSaver";
import { TemplateSelector } from "./TemplateSelector";
import { TemplateManager } from "./TemplateManager";
import { PrescriptionTemplate } from "@/fluxvet/hooks/usePrescriptionTemplates";
import { toast } from "react-toastify";
import { currentFeatures } from "@/configs/Settings";

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente da segunda etapa: Prescrições
 * Gerencia prescrição de tratamentos, exames, relatórios e orientações
 * Usa react-hook-form do contexto para manter dados persistentes
 */
export const PrescriptionsStep: React.FC = () => {
  // Busca os métodos do formulário do contexto
  const { methods, historyQuestionnaireResponse } = useVetAttendance();
  const { control, handleSubmit, watch, setValue } = methods;

  // Estado para controlar qual tab está ativa
  const [activeTab, setActiveTab] = useState(0);

  // Estado para controlar os modais de protocolo
  const [showTemplateSaver, setShowTemplateSaver] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  // Observa os dados do formulário para passar aos modais
  const treatments = watch("treatments") || [];
  const examsOrProcedures = watch("examsOrProcedures") || [];
  const reports = watch("reports") || [];
  const guidelines = watch("guidelines") || "";
  const selectedQuestionnaires = watch("selectedQuestionnaires") || [];

  // Estado para armazenar snapshot dos dados após salvar como protocolo
  const [lastSavedSnapshot, setLastSavedSnapshot] = useState<string | null>(
    null,
  );

  /**
   * Cria um snapshot (hash) dos dados atuais para comparação
   * Remove os IDs dos itens pois eles não são relevantes para a comparação
   */
  const currentDataSnapshot = useMemo(() => {
    const dataToCompare = {
      treatments: treatments.map(({ id, ...rest }) => rest),
      examsOrProcedures: examsOrProcedures.map(({ id, ...rest }) => rest),
      reports: reports.map(({ id, ...rest }) => rest),
      guidelines: guidelines || "",
      selectedQuestionnaires: [...selectedQuestionnaires].sort((a, b) =>
        a.questionnaireId.localeCompare(b.questionnaireId),
      ),
    };
    return JSON.stringify(dataToCompare);
  }, [treatments, examsOrProcedures, reports, guidelines, selectedQuestionnaires]);

  /**
   * Verifica se houve mudanças desde o último salvamento
   */
  const hasChangedSinceLastSave = useMemo(() => {
    if (!lastSavedSnapshot) return true; // Nunca salvou, botão habilitado
    return currentDataSnapshot !== lastSavedSnapshot;
  }, [currentDataSnapshot, lastSavedSnapshot]);

  /**
   * Handler para mudança de tab
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Handler de submissão do formulário (para teste)
   */
  const onSubmit = (data: any) => {
    console.log("Prescrições:", data);
  };

  /**
   * Aplica um protocolo de prescrição
   * Adiciona os itens do protocolo aos itens existentes (não substitui)
   */
  const handleApplyTemplate = (template: PrescriptionTemplate) => {
    let itemsAdded = 0;

    // Adiciona tratamentos do protocolo
    if (template.treatments && template.treatments.length > 0) {
      const newTreatments = template.treatments.map((t) => ({
        ...t,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      }));
      setValue("treatments", [...treatments, ...newTreatments]);
      itemsAdded += newTreatments.length;
    }

    // Adiciona exames/procedimentos do protocolo
    if (template.examsOrProcedures && template.examsOrProcedures.length > 0) {
      const newExams = template.examsOrProcedures.map((e) => ({
        ...e,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      }));
      setValue("examsOrProcedures", [...examsOrProcedures, ...newExams]);
      itemsAdded += newExams.length;
    }

    // Adiciona relatórios do protocolo
    if (template.reports && template.reports.length > 0) {
      const newReports = template.reports.map((r) => ({
        ...r,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      }));
      setValue("reports", [...reports, ...newReports]);
      itemsAdded += newReports.length;
    }

    // Adiciona orientações do protocolo (concatena com as existentes se houver)
    if (template.guidelines) {
      const currentGuidelines = guidelines || "";
      const separator = currentGuidelines ? "\n\n---\n\n" : "";
      setValue(
        "guidelines",
        currentGuidelines + separator + template.guidelines,
      );
      itemsAdded += 1;
    }

    // Mescla questionários do protocolo (sem duplicar por questionnaireId)
    if (template.questionnaires && template.questionnaires.length > 0) {
      const merged = [...selectedQuestionnaires];
      for (const q of template.questionnaires) {
        if (!merged.some((m) => m.questionnaireId === q.questionnaireId)) {
          merged.push({ ...q });
          itemsAdded += 1;
        }
      }
      setValue("selectedQuestionnaires", merged, { shouldDirty: true });
    }

    toast.success(
      `Protocolo "${template.name}" aplicado com sucesso! ${itemsAdded} ${
        itemsAdded === 1 ? "item adicionado" : "itens adicionados"
      }.`,
    );
  };

  /**
   * Verifica se há itens para salvar como protocolo
   */
  const hasItemsToSave =
    treatments.length > 0 ||
    examsOrProcedures.length > 0 ||
    reports.length > 0 ||
    !!guidelines ||
    selectedQuestionnaires.length > 0;

  /**
   * Handler chamado quando um protocolo é salvo com sucesso
   * Atualiza o snapshot para desabilitar o botão até próxima mudança
   */
  const handleTemplateSaved = () => {
    setLastSavedSnapshot(currentDataSnapshot);
    setShowTemplateSaver(false);
  };

  /** Exibe botões e modais de protocolo apenas quando !defaultApp */
  const showProtocolUI = !currentFeatures.defaultApp;

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: "100%" }}
      >
        {/* Botões de ação para protocolos — só quando !defaultApp */}
        {showProtocolUI && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="space-between"
            mb={2}
          >
            <Button
              variant="text"
              startIcon={<Settings />}
              onClick={() => setShowTemplateManager(true)}
              sx={{
                textTransform: "none",
                color: "#666",
                "&:hover": {
                  color: "#00A884",
                  bgcolor: "rgba(0, 168, 132, 0.08)",
                },
              }}
            >
              Gerenciar Protocolos
            </Button>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<CheckCircle />}
                onClick={() => setShowTemplateSelector(true)}
                sx={{
                  textTransform: "none",
                  borderColor: "#00A884",
                  color: "#00A884",
                  "&:hover": {
                    borderColor: "#008c6e",
                    bgcolor: "rgba(0, 168, 132, 0.08)",
                  },
                }}
              >
                Aplicar Protocolo
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={() => setShowTemplateSaver(true)}
                disabled={!hasItemsToSave || !hasChangedSinceLastSave}
                sx={{
                  textTransform: "none",
                  bgcolor: "#00A884",
                  "&:hover": { bgcolor: "#008c6e" },
                  "&.Mui-disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                Salvar como Protocolo
              </Button>
            </Stack>
          </Stack>
        )}

        <Paper sx={{ width: "100%", minHeight: 550 }}>
          {/* Tabs de navegação entre seções */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 64,
                textTransform: "none",
                fontSize: "0.9rem",
                fontWeight: 500,
              },
              "& .Mui-selected": {
                color: "#00A884 !important",
                fontWeight: 600,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#00A884",
                height: 3,
              },
            }}
          >
            <Tab
              icon={<Medication />}
              iconPosition="start"
              label="Tratamentos"
            />
            <Tab
              icon={<Science />}
              iconPosition="start"
              label="Exames/Procedimentos"
            />
            <Tab
              icon={<Description />}
              iconPosition="start"
              label="Relatórios"
            />
            <Tab
              icon={<Assignment />}
              iconPosition="start"
              label="Questionários"
            />
            <Tab
              icon={<InfoOutlined />}
              iconPosition="start"
              label="Orientações"
            />
          </Tabs>

          {/* Conteúdo da tab ativa */}
          <Box sx={{ p: 3, minHeight: 470 }}>
            {/* Tab 0: Tratamentos */}
            {activeTab === 0 && (
              <TreatmentsSection
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}

            {/* Tab 1: Exames ou Procedimentos */}
            {activeTab === 1 && (
              <ExamsProceduresSection
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}

            {/* Tab 2: Relatórios */}
            {activeTab === 2 && (
              <ReportsSection
                control={control}
                watch={watch}
                setValue={setValue}
              />
            )}

            {/* Tab 3: Questionários (seleção + visualização do histórico carregado) */}
            {activeTab === 3 && (
              <Stack spacing={3}>
                <VetProtocolQuestionnairesPicker
                  value={selectedQuestionnaires}
                  onChange={(next) =>
                    setValue("selectedQuestionnaires", next, { shouldDirty: true })
                  }
                />
                <VetPetQuestionnairesPanel
                  questionnaireResponses={historyQuestionnaireResponse || []}
                />
              </Stack>
            )}

            {/* Tab 4: Orientações */}
            {activeTab === 4 && <GuidelinesSection control={control} />}
          </Box>
        </Paper>
      </Box>

      {/* Modais de protocolo — só quando !defaultApp */}
      {showProtocolUI && (
        <>
          {showTemplateSaver && (
            <TemplateSaver
              open={showTemplateSaver}
              onClose={() => setShowTemplateSaver(false)}
              onSaveSuccess={handleTemplateSaved}
              treatments={treatments}
              examsOrProcedures={examsOrProcedures}
              reports={reports}
              guidelines={guidelines}
              questionnaires={selectedQuestionnaires}
            />
          )}
          {showTemplateSelector && (
            <TemplateSelector
              open={showTemplateSelector}
              onClose={() => setShowTemplateSelector(false)}
              onApply={handleApplyTemplate}
            />
          )}
          {showTemplateManager && (
            <TemplateManager
              open={showTemplateManager}
              onClose={() => setShowTemplateManager(false)}
            />
          )}
        </>
      )}
    </FormProvider>
  );
};

export default PrescriptionsStep;
